// =============================================================================
// request.ts — typed fetch helper for the Track 2 Worker (Patch 010.A.4
// R7 split from the monolithic lib/api.ts).
//
// Same-origin in production (VITE_API_URL empty). Local dev proxies through
// Vite to a wrangler dev session.
// =============================================================================

export const BASE = import.meta.env.VITE_API_URL ?? ''

export class ApiError extends Error {
  status: number
  body: unknown
  constructor(message: string, status: number, body: unknown) {
    super(message)
    this.status = status
    this.body = body
  }
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init?.headers ?? {}),
    },
  })
  const body = (await res.json().catch(() => null)) as unknown
  if (!res.ok) {
    // Patch 016.1.A — surface the API's `detail` text in the Error.message
    // when present, falling back to the error code, falling back to
    // http_<status>. Every ErrorCard across the app inherits this without a
    // call-site change. The structured error code stays on
    // ApiError.body.error for code that needs to branch on it.
    const code = (body as { error?: string })?.error
    const detail = (body as { detail?: string })?.detail
    const message =
      typeof detail === 'string' && detail.length > 0
        ? detail
        : code ?? `http_${res.status}`
    throw new ApiError(message, res.status, body)
  }
  if (body === null) {
    throw new ApiError('empty_response', res.status, body)
  }
  return body as T
}
